import React, { PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Centrifuge, UnauthorizedError } from 'centrifuge';
import { ApiContext } from '@api/api';
import { useAuthReady } from '@hooks/useAuthReady';

/**
 * Single Centrifugo connection for the whole app. The BE-minted token carries the channel list
 * (broadcast:app, personal:#<userId>, company:<companyId>) — Centrifugo subscribes the connection
 * server-side, so publications from ALL channels arrive on the connection itself and funnel into
 * the registered listeners. Messages are thin invalidation hints; consumers refetch via REST and
 * MUST also refetch on reconnect (lost messages are harmless by design).
 *
 * Failure behavior (never "stuck forever"):
 * - BE reports enabled=false (push not configured) → no connection at all; re-checked every 60s.
 * - Centrifugo unreachable (404/refused) → centrifuge-js retries with capped exponential backoff
 *   (~0.5s..20s, cheap); connectionState stays 'connecting'/'disconnected' so consumers can run
 *   their own degraded-mode fallback (see ExpeditionMultiDuplicationJobDetail's safety poll).
 */

export interface PushMessage {
  type: string;
  [key: string]: any;
}

type PushListener = (message: PushMessage) => void;
type ReconnectListener = () => void;

export interface ChannelSubscriptionHandlers {
  onPublication: (message: PushMessage) => void;
  /** live=true when the subscription is confirmed by the server; use for poll-fallback decisions. */
  onStateChange?: (live: boolean) => void;
}

interface PushContextType {
  /** Register a listener for all incoming push messages; returns an unsubscribe function. */
  addListener: (onMessage: PushListener, onReconnect?: ReconnectListener) => () => void;
  /**
   * Client-side subscription to a per-entity channel (e.g. job:<jobId>) authorized by a BE-minted
   * subscription token. Returns an unsubscribe function. Safe to call while push is disabled or
   * still connecting — the subscription attaches when the connection exists.
   */
  subscribeChannel: (channel: string, handlers: ChannelSubscriptionHandlers) => () => void;
  /** 'disabled' | 'disconnected' | 'connecting' | 'connected' */
  connectionState: string;
  reconnectCount: number;
}

export const PushContext = createContext<PushContextType>({
  addListener: () => () => {},
  subscribeChannel: () => () => {},
  connectionState: 'disabled',
  reconnectCount: 0,
});

const ENABLED_RECHECK_MS = 60000;

interface ChannelEntry {
  handlers: Set<ChannelSubscriptionHandlers>;
  sub: ReturnType<Centrifuge['newSubscription']> | null;
}

export const PushProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { getPushToken, getPushSubscriptionToken } = useContext(ApiContext);
  const authReady = useAuthReady();
  const [connectionState, setConnectionState] = useState('disabled');
  const [reconnectCount, setReconnectCount] = useState(0);

  const listenersRef = useRef<Set<{ onMessage: PushListener; onReconnect?: ReconnectListener }>>(new Set());
  const channelsRef = useRef<Map<string, ChannelEntry>>(new Map());
  const wasConnectedRef = useRef(false);
  const centrifugeRef = useRef<Centrifuge | null>(null);
  const getPushTokenRef = useRef(getPushToken);
  getPushTokenRef.current = getPushToken;
  const getPushSubscriptionTokenRef = useRef(getPushSubscriptionToken);
  getPushSubscriptionTokenRef.current = getPushSubscriptionToken;
  const ensureChannelSubRef = useRef<((channel: string) => void) | null>(null);

  // Connecting needs a session: the push token endpoint is authenticated, and this provider mounts
  // above the router's auth guard. Re-runs (and reconnects) when the user logs in.
  // (Sentry RINGIL-APP-WM.)
  useEffect(() => {
    if (!authReady) return;
    let disposed = false;
    let recheckTimer: number | null = null;

    const getToken = () =>
      new Promise<string>((resolve, reject) => {
        try {
          getPushTokenRef.current(
            d => {
              if (d.enabled && d.token) {
                resolve(d.token);
              } else {
                // Push got disabled server-side AFTER we connected (config change + BE restart).
                // UnauthorizedError stops centrifuge-js's transport retry loop (ws/http_stream/sse
                // would otherwise hammer forever); teardown returns us to the disabled+recheck state.
                teardownToDisabled();
                reject(new UnauthorizedError('push disabled'));
              }
            },
            null,
            { onError: err => reject(err ?? new Error('push token failed')) },
          );
        } catch (e) {
          reject(e);
        }
      });

    const connect = () => {
      const origin = window.location.origin;
      const centrifuge = new Centrifuge(
        [
          { transport: 'websocket', endpoint: `${origin.replace(/^http/, 'ws')}/centrifugo/connection/websocket` },
          { transport: 'http_stream', endpoint: `${origin}/centrifugo/connection/http_stream` },
          { transport: 'sse', endpoint: `${origin}/centrifugo/connection/sse` },
        ],
        {
          getToken,
          emulationEndpoint: `${origin}/centrifugo/emulation`,
        },
      );
      centrifugeRef.current = centrifuge;

      centrifuge.on('connected', () => {
        setConnectionState('connected');
        if (wasConnectedRef.current) {
          setReconnectCount(c => c + 1);
          listenersRef.current.forEach(l => l.onReconnect?.());
        }
        wasConnectedRef.current = true;
      });
      centrifuge.on('connecting', () => setConnectionState('connecting'));
      centrifuge.on('disconnected', () => setConnectionState('disconnected'));
      // MUST be registered: the bundled EventEmitter THROWS on an unhandled 'error' event, which
      // would abort centrifuge-js's own reconnect scheduling (transport fallback, token retry)
      // and freeze the client in 'connecting' for the session.
      centrifuge.on('error', e => console.warn('PushContext: centrifugo client error', e));

      // Server-side subscriptions deliver on the connection itself
      centrifuge.on('publication', ctx => {
        const data = ctx.data as PushMessage;
        if (data?.type == null) return;
        listenersRef.current.forEach(l => {
          try {
            l.onMessage(data);
          } catch (e) {
            console.error('PushContext: listener failed', e);
          }
        });
      });

      centrifuge.connect();
      // Attach any channel subscriptions requested before the connection existed
      channelsRef.current.forEach((_, channel) => ensureChannelSub(channel));
    };

    // exposed via ref so subscribeChannel (stable identity) can reach the latest closure
    ensureChannelSubRef.current = (channel: string) => {
      const centrifuge = centrifugeRef.current;
      const entry = channelsRef.current.get(channel);
      if (centrifuge == null || entry == null || entry.sub != null) return;
      const existing = centrifuge.getSubscription(channel);
      const sub =
        existing ??
        centrifuge.newSubscription(channel, {
          getToken: () =>
            new Promise<string>((resolve, reject) => {
              try {
                getPushSubscriptionTokenRef.current(
                  channel,
                  d => (d.enabled && d.token ? resolve(d.token) : reject(new Error('push disabled'))),
                  null,
                  { onError: err => reject(err ?? new Error('subscription token failed')) },
                );
              } catch (e) {
                reject(e);
              }
            }),
        });
      entry.sub = sub;
      // Same throw-on-unhandled-'error' hazard as the connection: without this, a failed
      // subscription-token fetch would skip the resubscribe scheduling.
      sub.on('error', e => console.warn(`PushContext: subscription error channel=${channel}`, e));
      sub.on('publication', ctx => {
        const data = ctx.data as PushMessage;
        if (data?.type == null) return;
        channelsRef.current.get(channel)?.handlers.forEach(h => {
          try {
            h.onPublication(data);
          } catch (e) {
            console.error('PushContext: channel listener failed', e);
          }
        });
      });
      sub.on('subscribed', () => channelsRef.current.get(channel)?.handlers.forEach(h => h.onStateChange?.(true)));
      sub.on('unsubscribed', () => channelsRef.current.get(channel)?.handlers.forEach(h => h.onStateChange?.(false)));
      sub.on('subscribing', () => channelsRef.current.get(channel)?.handlers.forEach(h => h.onStateChange?.(false)));
      sub.subscribe();
    };
    const ensureChannelSub = (channel: string) => ensureChannelSubRef.current?.(channel);

    // Only connect when the BE says push is configured; otherwise stay 'disabled' and
    // re-check occasionally so enabling push server-side doesn't require a page reload.
    const checkEnabledAndConnect = () => {
      getPushTokenRef.current(
        d => {
          if (disposed) return;
          if (d.enabled) {
            connect();
          } else {
            setConnectionState('disabled');
            recheckTimer = window.setTimeout(checkEnabledAndConnect, ENABLED_RECHECK_MS);
          }
        },
        null,
        {
          onError: () => {
            if (disposed) return;
            setConnectionState('disabled');
            recheckTimer = window.setTimeout(checkEnabledAndConnect, ENABLED_RECHECK_MS);
          },
        },
      );
    };

    // Dispose the client and fall back to the disabled+recheck loop. Channel sub handles are
    // cleared so a later re-enable re-attaches them on the fresh connection (connect() replays
    // channelsRef); wasConnectedRef stays true so listeners refetch via onReconnect when it does.
    const teardownToDisabled = () => {
      if (disposed) return;
      const centrifuge = centrifugeRef.current;
      centrifugeRef.current = null;
      channelsRef.current.forEach(entry => {
        entry.sub = null;
      });
      try {
        centrifuge?.disconnect();
      } catch {
        // best effort — the client may already be torn down
      }
      setConnectionState('disabled');
      if (recheckTimer != null) window.clearTimeout(recheckTimer);
      recheckTimer = window.setTimeout(checkEnabledAndConnect, ENABLED_RECHECK_MS);
    };

    checkEnabledAndConnect();

    return () => {
      disposed = true;
      if (recheckTimer != null) window.clearTimeout(recheckTimer);
      centrifugeRef.current?.disconnect();
      centrifugeRef.current = null;
    };
  }, [authReady]);

  const addListener = (onMessage: PushListener, onReconnect?: ReconnectListener) => {
    const entry = { onMessage, onReconnect };
    listenersRef.current.add(entry);
    return () => {
      listenersRef.current.delete(entry);
    };
  };

  const subscribeChannel = (channel: string, handlers: ChannelSubscriptionHandlers) => {
    let entry = channelsRef.current.get(channel);
    if (entry == null) {
      entry = { handlers: new Set(), sub: null };
      channelsRef.current.set(channel, entry);
    }
    entry.handlers.add(handlers);
    ensureChannelSubRef.current?.(channel);
    return () => {
      const current = channelsRef.current.get(channel);
      if (current == null) return;
      current.handlers.delete(handlers);
      if (current.handlers.size === 0) {
        if (current.sub != null) {
          current.sub.unsubscribe();
          centrifugeRef.current?.removeSubscription(current.sub);
        }
        channelsRef.current.delete(channel);
      }
    };
  };

  return <PushContext.Provider value={{ addListener, subscribeChannel, connectionState, reconnectCount }}>{children}</PushContext.Provider>;
};
